import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, Mail, MapPin, DollarSign, CreditCard, Eye, EyeOff, User } from 'lucide-react';
import { Collaborator } from '../../types/collaborators';
import { collaboratorsService } from '../../services/supabaseService';
import { notificationService } from '../../services/notificationService';
import CollaboratorModal from './CollaboratorModal';
import InviteCollaboratorModal from '../InviteCollaboratorModal';
import Avatar from '../Avatar';
import { usePermissions } from '../../hooks/usePermissions';
import authService from '../../services/authService';
import { supabase } from '../../services/supabaseService';
import { rolesService } from '../../services/rolesService';

const CollaboratorsList: React.FC = () => {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [currentCollaborator, setCurrentCollaborator] = useState<Collaborator | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState<Collaborator | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState<Collaborator | null>(null);

  const { isAdmin, isCollaboradorProgramador, user: permissionUser } = usePermissions();
  const isProgrammerView = isCollaboradorProgramador();
  const userRoleName = permissionUser?.user_role?.name;

  const loadCollaborators = async () => {
    try {
      setLoading(true);

      const currentUserId = authService.getCurrentSession()?.user?.id;
      const isProgrammer = userRoleName === 'colaborador_programador';

      if (isProgrammer && currentUserId) {
        // For programmer view, only load their own data
        const collaboratorsData = await collaboratorsService.getAll();
        const currentCollab = collaboratorsData.find(c => c.id === currentUserId);

        if (currentCollab) {
          setCurrentCollaborator(currentCollab);
          setCollaborators([currentCollab]);
        }
      } else {
        // For admin view, load all collaborators
        const collaboratorsData = await collaboratorsService.getAll();
        setCollaborators(collaboratorsData);
      }
    } catch (error) {
      console.error('Error loading collaborators:', error);
      setCollaborators([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollaborators();
  }, [userRoleName]);

  const handleCreateCollaborator = () => {
    setEditingCollaborator(null);
    setIsModalOpen(true);
  };

  const handleEditCollaborator = (collaborator: Collaborator) => {
    setEditingCollaborator(collaborator);
    setIsModalOpen(true);
  };

  const handleSaveCollaborator = (
    collaboratorData: Partial<Collaborator>,
    invite?: { roleId: string }
  ) => {
    if (editingCollaborator) {
      // Update existing
      updateCollaborator(collaboratorData);
    } else {
      // Create new (con o sin invitación inmediata)
      createCollaborator(collaboratorData, invite);
    }
  };

  const handleInviteToCRM = (collaborator: Collaborator) => {
    setSelectedCollaborator(collaborator);
    setInviteModalOpen(true);
  };

  const handleInviteSuccess = () => {
    loadCollaborators();
    setInviteModalOpen(false);
    setSelectedCollaborator(null);
  };

  const createCollaborator = async (
    collaboratorData: Partial<Collaborator>,
    invite?: { roleId: string }
  ) => {
    try {
      console.log('🔄 Creando colaborador en Supabase:', collaboratorData.name);
      const newCollaborator = await collaboratorsService.create({
        name: collaboratorData.name || '',
        email: collaboratorData.email || '',
        country: collaboratorData.country || '',
        hourlyRate: collaboratorData.hourlyRate || 0,
        paymentMethod: collaboratorData.paymentMethod || 'bank_transfer',
        currency: collaboratorData.currency || 'EUR',
        isActive: collaboratorData.isActive !== false
      });

      setCollaborators([newCollaborator, ...collaborators]);
      setIsModalOpen(false);
      setEditingCollaborator(null);

      await notificationService.createNotification({
        code: 'COLLABORATOR.CREATED',
        title: 'Colaborador Creado',
        body: `${collaboratorData.name} ha sido añadido a la plantilla`,
        severity: 'info',
        data: { user_id: newCollaborator.id, collaborator_name: collaboratorData.name }
      });

      if (invite?.roleId) {
        const invitedBy = authService.getCurrentSession()?.user?.id;
        if (!invitedBy) {
          alert('✅ Colaborador creado, pero no pude enviar la invitación (sesión).');
          return;
        }
        try {
          await rolesService.createInvitation(
            newCollaborator.email,
            newCollaborator.id,
            invite.roleId,
            invitedBy
          );
          await rolesService.grantCRMAccess(newCollaborator.id, invite.roleId, invitedBy);
          alert(`✅ Colaborador creado. Invitación enviada a ${newCollaborator.email}.`);
          await loadCollaborators();
        } catch (inviteErr: any) {
          console.error('Error enviando invitación:', inviteErr);
          alert(
            '✅ Colaborador creado, pero la invitación falló: ' +
            (inviteErr?.message || 'error desconocido') +
            '. Puedes reintentar desde el botón "Invitar al CRM".'
          );
        }
      } else {
        alert('✅ Colaborador creado correctamente');
      }
    } catch (error) {
      console.error('Error creating collaborator:', error);
      alert('❌ Error al crear el colaborador');
    }
  };

  const updateCollaborator = async (collaboratorData: Partial<Collaborator>) => {
    if (!editingCollaborator) return;
    
    try {
      console.log('🔄 Actualizando colaborador en Supabase:', editingCollaborator.id);
      const updatedCollaborator = await collaboratorsService.update(editingCollaborator.id, {
        name: collaboratorData.name,
        email: collaboratorData.email,
        country: collaboratorData.country,
        hourlyRate: collaboratorData.hourlyRate,
        paymentMethod: collaboratorData.paymentMethod,
        currency: collaboratorData.currency,
        isActive: collaboratorData.isActive
      });
      
      setCollaborators(collaborators.map(c =>
        c.id === editingCollaborator.id ? updatedCollaborator : c
      ));
      setIsModalOpen(false);
      setEditingCollaborator(null);
      alert('✅ Colaborador actualizado correctamente');

      await notificationService.createNotification({
        code: 'COLLABORATOR.UPDATED',
        title: 'Colaborador Actualizado',
        body: `Se actualizó la información de ${collaboratorData.name}`,
        severity: 'info',
        data: { user_id: editingCollaborator.id, collaborator_name: collaboratorData.name }
      });
    } catch (error) {
      console.error('Error updating collaborator:', error);
      alert('❌ Error al actualizar el colaborador');
    }
  };

  const handleToggleStatus = (collaboratorId: string) => {
    const collaborator = collaborators.find(c => c.id === collaboratorId);
    if (collaborator) {
      updateCollaboratorStatus(collaboratorId, !collaborator.isActive);
    }
  };

  const updateCollaboratorStatus = async (collaboratorId: string, isActive: boolean) => {
    try {
      console.log('🔄 Actualizando estado de colaborador:', collaboratorId, 'activo:', isActive);
      await collaboratorsService.update(collaboratorId, { isActive });
      
      // Update local state immediately
      setCollaborators(prev => prev.map(c =>
        c.id === collaboratorId ? { ...c, isActive } : c
      ));

      const collaborator = collaborators.find(c => c.id === collaboratorId);
      await notificationService.createNotification({
        code: 'COLLABORATOR.STATUS_CHANGED',
        title: 'Estado de Colaborador Cambiado',
        body: `${collaborator?.name} está ahora ${isActive ? 'activo' : 'inactivo'}`,
        severity: 'info',
        data: { user_id: collaboratorId, is_active: isActive }
      });

      console.log('✅ Estado de colaborador actualizado');
    } catch (error) {
      console.error('Error updating collaborator status:', error);
      alert('❌ Error al actualizar el estado del colaborador');
    }
  };

  const handleDeleteCollaborator = async (collaboratorId: string) => {
    const collaborator = collaborators.find(c => c.id === collaboratorId);
    if (collaborator && window.confirm(`¿Eliminar a ${collaborator.name}? Esta acción no se puede deshacer.`)) {
      try {
        console.log('🔄 Eliminando colaborador:', collaboratorId);
        await collaboratorsService.delete(collaboratorId);
        setCollaborators(collaborators.filter(c => c.id !== collaboratorId));
        alert('✅ Colaborador eliminado correctamente');

        await notificationService.createNotification({
          code: 'COLLABORATOR.DELETED',
          title: 'Colaborador Eliminado',
          body: `${collaborator.name} ha sido eliminado de la plantilla`,
          severity: 'warn',
          data: { collaborator_name: collaborator.name }
        });
      } catch (error) {
        console.error('Error deleting collaborator:', error);
        alert('❌ Error al eliminar el colaborador');
      }
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods = {
      bank_transfer: 'Transferencia Bancaria',
      paypal: 'PayPal',
      wise: 'Wise',
      crypto: 'Criptomonedas',
      other: 'Otro'
    };
    return methods[method as keyof typeof methods] || method;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando colaboradores...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {isProgrammerView ? 'Mi Información' : 'Plantilla'}
          </h2>
          <p className="text-gray-600">
            {isProgrammerView
              ? 'Visualiza tu información como colaborador'
              : 'Gestiona los colaboradores externos y sus tarifas'
            }
          </p>
        </div>
        {!isProgrammerView && (
          <button
            onClick={handleCreateCollaborator}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Nuevo Colaborador</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {isProgrammerView ? (
          <>
            {/* Programmer View: Personal Stats */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Mi Estado</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {currentCollaborator?.isActive ? 'Activo' : 'Inactivo'}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Mi Tarifa</p>
                  <p className="text-2xl font-bold text-green-900">
                    {currentCollaborator
                      ? `${currentCollaborator.hourlyRate} ${currentCollaborator.currency}/h`
                      : 'N/A'
                    }
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Método de Pago</p>
                  <p className="text-lg font-bold text-orange-900">
                    {currentCollaborator
                      ? getPaymentMethodLabel(currentCollaborator.paymentMethod)
                      : 'N/A'
                    }
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Admin View: Team Stats */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Total Plantilla</p>
                  <p className="text-2xl font-bold text-blue-900">{collaborators.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Plantilla Activos</p>
                  <p className="text-2xl font-bold text-green-900">
                    {collaborators.filter(c => c.isActive).length}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Tarifa Promedio</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {collaborators.length > 0
                      ? Math.round(collaborators.reduce((sum, c) => sum + c.hourlyRate, 0) / collaborators.length)
                      : 0
                    }€/h
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Collaborators View */}
      {isProgrammerView ? (
        /* Programmer Profile Card View */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {currentCollaborator ? (
            <div className="p-8">
              <div className="flex items-start space-x-6 mb-8">
                <div className="flex-shrink-0">
                  <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-3xl">
                      {currentCollaborator.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentCollaborator.name}</h2>
                  <div className="flex items-center space-x-2 text-gray-600 mb-4">
                    <Mail className="w-4 h-4" />
                    <span>{currentCollaborator.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`inline-flex items-center space-x-1 px-3 py-1 text-sm font-semibold rounded-full ${
                        currentCollaborator.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {currentCollaborator.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      <span>{currentCollaborator.isActive ? 'Cuenta Activa' : 'Cuenta Inactiva'}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">País</p>
                      <p className="text-lg font-semibold text-gray-900">{currentCollaborator.country}</p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tarifa por Hora</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {currentCollaborator.hourlyRate} {currentCollaborator.currency}/h
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <CreditCard className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Método de Pago</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {getPaymentMethodLabel(currentCollaborator.paymentMethod)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Moneda</p>
                      <p className="text-lg font-semibold text-gray-900">{currentCollaborator.currency}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> Esta información es solo de lectura. Si necesitas actualizar algún dato, por favor contacta con el administrador.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontró información</h3>
              <p className="text-gray-500">No hay información de colaborador disponible</p>
            </div>
          )}
        </div>
      ) : (
        /* Admin Table View */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Colaborador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    País
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarifa/Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Método de Pago
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {collaborators.map((collaborator) => (
                <tr key={collaborator.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Avatar
                        name={collaborator.name}
                        src={(collaborator as any).avatar}
                        size="md"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{collaborator.name}</div>
                        <div className="text-sm text-gray-500 flex items-center space-x-1">
                          <Mail className="w-3 h-3" />
                          <span>{collaborator.email}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{collaborator.country}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {collaborator.hourlyRate} {collaborator.currency}/h
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {getPaymentMethodLabel(collaborator.paymentMethod)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!isProgrammerView ? (
                      <button
                        onClick={() => handleToggleStatus(collaborator.id)}
                        className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full ${
                          collaborator.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {collaborator.isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        <span>{collaborator.isActive ? 'Activo' : 'Inactivo'}</span>
                      </button>
                    ) : (
                      <span
                        className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full ${
                          collaborator.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {collaborator.isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        <span>{collaborator.isActive ? 'Activo' : 'Inactivo'}</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {!isProgrammerView && (
                        <>
                          <button
                            onClick={() => handleEditCollaborator(collaborator)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Editar colaborador"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCollaborator(collaborator.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Eliminar colaborador"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {collaborators.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay colaboradores</h3>
            <p className="text-gray-500 mb-6">Comienza añadiendo tu primer colaborador externo</p>
            <button
              onClick={handleCreateCollaborator}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo Colaborador</span>
            </button>
          </div>
        )}
      </div>
      )}

      {/* Modals */}
      <CollaboratorModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCollaborator(null);
        }}
        onSave={handleSaveCollaborator}
        collaborator={editingCollaborator}
        onInviteToCRM={handleInviteToCRM}
      />

      {selectedCollaborator && (
        <InviteCollaboratorModal
          isOpen={inviteModalOpen}
          onClose={() => {
            setInviteModalOpen(false);
            setSelectedCollaborator(null);
          }}
          collaborator={selectedCollaborator}
          onSuccess={handleInviteSuccess}
        />
      )}
    </div>
  );
};

export default CollaboratorsList;